import React from 'react';
import { FaPlus, FaVideo, FaCalendarCheck, FaClock, FaUsers } from 'react-icons/fa';

const getWebinarStatusBadge = (status) => {
  const styles = {
    pending_approval: 'bg-amber-100 text-amber-800',
    upcoming: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    rejected: 'bg-red-100 text-red-800',
    cancelled: 'bg-red-100 text-red-800'
  };
  const labels = {
    pending_approval: 'Pending Review',
    upcoming: 'Approved',
    completed: 'Completed',
    rejected: 'Not Approved',
    cancelled: 'Cancelled'
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {labels[status] || status}
    </span>
  );
};

export const ProDashWebinarsTab = ({
  myWebinars,
  showWebinarForm,
  setShowWebinarForm,
  webinarForm,
  setWebinarForm,
  handleWebinarSubmit,
  webinarSubmitting,
}) => (
  <div className="space-y-6">
    {/* Header with Propose Button */}
    <div className="flex items-center justify-between">
      <div>
        <h3 className="text-xl font-bold text-gray-900">My Webinars</h3>
        <p className="text-gray-500 text-sm">Propose and manage your educational webinars</p>
      </div>
      <button
        onClick={() => setShowWebinarForm(true)}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        data-testid="propose-webinar-btn"
      >
        <FaPlus />
        Propose Webinar
      </button>
    </div>

    {/* Webinar Proposal Form Modal */}
    {showWebinarForm && (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-testid="webinar-proposal-form">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Propose a Webinar</h3>
              <button
                onClick={() => setShowWebinarForm(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <p className="text-gray-500 text-sm mt-1">Your proposal will be reviewed by an admin before going live.</p>
          </div>

          <form onSubmit={handleWebinarSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input
                type="text"
                required
                value={webinarForm.title}
                onChange={(e) => setWebinarForm({...webinarForm, title: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Building Your Family Trust: A Complete Guide"
                data-testid="webinar-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                required
                value={webinarForm.description}
                onChange={(e) => setWebinarForm({...webinarForm, description: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                placeholder="Describe what attendees will learn..."
                data-testid="webinar-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  required
                  value={webinarForm.date}
                  onChange={(e) => setWebinarForm({...webinarForm, date: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="webinar-date-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time *</label>
                <input
                  type="time"
                  required
                  value={webinarForm.time}
                  onChange={(e) => setWebinarForm({...webinarForm, time: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  data-testid="webinar-time-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                <select
                  value={webinarForm.duration_minutes}
                  onChange={(e) => setWebinarForm({...webinarForm, duration_minutes: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Attendees</label>
                <input
                  type="number"
                  value={webinarForm.max_attendees}
                  onChange={(e) => setWebinarForm({...webinarForm, max_attendees: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min={10}
                  max={500}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                required
                value={webinarForm.category}
                onChange={(e) => setWebinarForm({...webinarForm, category: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                data-testid="webinar-category-select"
              >
                <option value="">Select a category</option>
                <option value="Estate Planning">Estate Planning</option>
                <option value="Tax Planning">Tax Planning</option>
                <option value="Real Estate">Real Estate</option>
                <option value="Family Governance">Family Governance</option>
                <option value="Wealth Management">Wealth Management</option>
                <option value="Insurance">Insurance</option>
                <option value="Retirement Planning">Retirement Planning</option>
                <option value="Business Succession">Business Succession</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Link (optional)</label>
              <input
                type="url"
                value={webinarForm.meeting_link}
                onChange={(e) => setWebinarForm({...webinarForm, meeting_link: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://zoom.us/j/..."
              />
              <p className="text-xs text-gray-500 mt-1">You can add this later if you don't have it yet.</p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowWebinarForm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={webinarSubmitting}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                data-testid="submit-webinar-btn"
              >
                {webinarSubmitting ? 'Submitting...' : 'Submit Proposal'}
              </button>
            </div>
          </form>
        </div>
      </div>
    )}

    {/* Webinars List */}
    <div className="bg-white rounded-xl shadow-sm p-6">
      {myWebinars.length === 0 ? (
        <div className="text-center py-12">
          <FaVideo className="text-4xl text-gray-300 mx-auto mb-4" />
          <h4 className="font-medium text-gray-900 mb-2">No Webinars Yet</h4>
          <p className="text-gray-500 mb-4">Propose your first webinar to share your expertise with Legacy Forge families.</p>
          <button
            onClick={() => setShowWebinarForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Propose Your First Webinar
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {myWebinars.map((webinar) => (
            <div
              key={webinar.webinar_id}
              className="border rounded-lg p-4 hover:bg-gray-50"
              data-testid={`webinar-${webinar.webinar_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-gray-900">{webinar.title}</h4>
                    {getWebinarStatusBadge(webinar.status)}
                  </div>
                  <p className="text-sm text-gray-500 mb-2">{webinar.description?.substring(0, 150)}...</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <FaCalendarCheck className="text-gray-400" />
                      {webinar.date} at {webinar.time}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaClock className="text-gray-400" />
                      {webinar.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <FaUsers className="text-gray-400" />
                      {webinar.registered_count || 0} / {webinar.max_attendees}
                    </span>
                  </div>
                  {webinar.status === 'rejected' && webinar.rejection_reason && (
                    <p className="mt-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                      <strong>Reason:</strong> {webinar.rejection_reason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
